import React, { useCallback, useContext, useState } from "react"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"

import { geminiLanguageOptions } from "src/geminiLanguageOptions"
import { PLAYBACK_RATE_OPTIONS } from "src/playbackRateOptions"

import ModalWithProvidedBody from "components/ModalWithProvidedBody"

import CloudroomContext from "../CloudroomContext"

const AdminAudioControls: React.FC = () => {
  const {
    activeLanguageCode,
    setActiveLanguageCode,
    playbackRate,
    setPlaybackRate,
  } = useContext(CloudroomContext)

  const [languageCode, setLanguageCode] = useState(
    () => geminiLanguageOptions[0].value
  )

  const stopTranslation = useCallback(() => {
    setActiveLanguageCode(null)
  }, [setActiveLanguageCode])

  const startTranslation = useCallback(
    (closeModal: () => void) => {
      setActiveLanguageCode(languageCode)
      closeModal()
    },
    [languageCode, setActiveLanguageCode]
  )

  return (
    <ModalWithProvidedBody
      buttonText="Audio controls"
      buttonClassName="btn solid green btn-success mb-1"
      modalClassName="bootstrap-modal"
      modalTitle="Audio controls"
    >
      {({ closeModal }) => (
        <div className="modal-body">
          <AlertMessageBox level={AlertLevel.Info}>
            Playback speed must be set to 1x in order to translate.
          </AlertMessageBox>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label
              htmlFor="admin-translation-language"
              className="mb-0 text-sm leading-none text-gray-700"
            >
              Translate to
            </label>
            <select
              id="admin-translation-language"
              className="max-w-[min(100%,20rem)] rounded border border-gray-300 bg-white px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              value={languageCode}
              onChange={event => setLanguageCode(event.target.value)}
              disabled={playbackRate !== 1}
            >
              {geminiLanguageOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {activeLanguageCode ? (
              <button
                type="button"
                className="btn btn-sm solid green btn-danger"
                onClick={() => stopTranslation()}
              >
                Stop
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm solid green btn-success"
                onClick={() => startTranslation(closeModal)}
                disabled={playbackRate !== 1}
              >
                Start
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor="admin-audio-playback-rate"
              className="mb-0 text-sm leading-none text-gray-700"
            >
              Audio playback speed
            </label>
            <select
              id="admin-audio-playback-rate"
              className="max-w-[min(100%,20rem)] rounded border border-gray-300 bg-white px-2 py-1 text-sm"
              value={playbackRate}
              onChange={event => {
                setPlaybackRate(parseFloat(event.target.value))
                closeModal()
              }}
              disabled={!!activeLanguageCode}
            >
              {PLAYBACK_RATE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </ModalWithProvidedBody>
  )
}

export default AdminAudioControls
